/**
 * Jeu d'icônes au trait, partagé par tout le parcours client.
 *
 * Pourquoi ce fichier existe (11/09/2026) : le widget de devis et le widget de rendez-vous sont
 * deux IIFE indépendantes, sans état partagé. Les vignettes de raccourci n'existaient que dans le
 * premier, et chaque fichier portait sa propre liste d'émojis. Résultat : le client voyait des
 * choses différentes selon le bouton sur lequel il cliquait, et les émojis s'affichaient en
 * couleur, différemment sur iPhone, Android et Windows — rien à voir avec le reste du site.
 * Une seule source ici, comme mauto-calendrier.js pour les règles de rendez-vous et
 * mauto-pneu-dimensions.js pour les listes de dimensions.
 *
 * Toutes les icônes sont tracées en `stroke="currentColor"`, sans remplissage : la même icône est
 * claire sur le bandeau noir de l'accueil et sombre sur fond clair dans les overlays. Ne jamais
 * leur imposer une couleur fixe, ce serait perdre cette propriété.
 *
 * La taille est pilotée par le CSS, pas par le SVG. Par défaut `.mauto-ico` fait 1em et s'aligne
 * sur la ligne de texte (icônes posées dans une phrase, un bouton, un libellé). Les contextes qui
 * veulent plus grand — vignettes de raccourci, boutons de saison — surchargent avec un sélecteur
 * plus spécifique dans devis-widget.css.
 *
 * Ce fichier doit être chargé AVANT devis-widget.js et devis-rdv-widget.js : les scripts différés
 * s'exécutent dans l'ordre du document. S'il manque, les widgets continuent de fonctionner —
 * `pour()` renvoie une chaîne vide et seuls les libellés s'affichent.
 */
(function () {
  'use strict';

  function ico(corps) {
    return '<svg class="mauto-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" ' +
      'focusable="false">' + corps + '</svg>';
  }

  var ICONES = {

    // ── Prestations : les 6 vignettes de raccourci ─────────────────────────────────────────
    // Formes volontairement grosses : à 28 px, un détail fin devient une tache.

    // Burette d'huile : corps, bec verseur, poignée et goutte qui tombe.
    vidange: ico(
      '<path d="M2.8 12.8h9.6v5.4a1.6 1.6 0 0 1-1.6 1.6H4.4a1.6 1.6 0 0 1-1.6-1.6z"/>' +
      '<path d="M12.4 14.8h3l4.6-4.6"/>' +
      '<path d="M5.6 12.8v-2.2h4.2v2.2"/>' +
      '<path d="M18.6 3c-1.3 1.5-2 2.6-2 3.4a2 2 0 0 0 4 0c0-.8-.7-1.9-2-3.4z"/>'
    ),
    // Amortisseur : œillet haut, ressort hélicoïdal, œillet bas.
    amortisseurs: ico(
      '<circle cx="12" cy="3.6" r="1.9"/>' +
      '<circle cx="12" cy="20.4" r="1.9"/>' +
      '<path d="M12 5.5v1.4"/>' +
      '<path d="M8.3 7.4h7.4l-7.4 2.7h7.4l-7.4 2.7h7.4l-7.4 2.7h7.4"/>' +
      '<path d="M12 15.5v3"/>'
    ),
    // Disque de frein ventilé + étrier.
    freinage: ico(
      '<circle cx="10.5" cy="12" r="7.5"/>' +
      '<circle cx="10.5" cy="12" r="2.8"/>' +
      '<path d="M10.5 4.5v2M10.5 17.5v2M3 12h2M16 12h2"/>' +
      '<path d="M17.6 8.4h1.9A1.5 1.5 0 0 1 21 9.9v4.2a1.5 1.5 0 0 1-1.5 1.5h-1.9"/>'
    ),
    // Pneu : bande de roulement, flanc, jante, et quatre sculptures.
    // Les sculptures restent DANS la bande (entre les deux grands cercles) : posées jusqu'au
    // moyeu, elles faisaient lire un engrenage plutôt qu'un pneu.
    pneus: ico(
      '<circle cx="12" cy="12" r="9"/>' +
      '<circle cx="12" cy="12" r="5.8"/>' +
      '<circle cx="12" cy="12" r="2.4"/>' +
      '<path d="M12 3v3.2M12 17.8V21M3 12h3.2M17.8 12H21"/>'
    ),
    // Distribution : deux poulies reliées par la courroie.
    distribution: ico(
      '<circle cx="8" cy="8" r="4.2"/>' +
      '<circle cx="8" cy="8" r="1.1"/>' +
      '<circle cx="17" cy="16" r="3.2"/>' +
      '<circle cx="17" cy="16" r="0.9"/>' +
      '<path d="M4.8 10.7 14.5 18"/>' +
      '<path d="M11.2 5.3 19.7 13.2"/>'
    ),
    // Pare-brise : vitrage et essuie-glace.
    pare_brise: ico(
      '<path d="M3.6 15.6 6 7.8a2 2 0 0 1 1.9-1.4h8.2A2 2 0 0 1 18 7.8l2.4 7.8a1 1 0 0 1-1 1.3H4.6a1 1 0 0 1-1-1.3z"/>' +
      '<path d="M7.4 16.9 12.8 9.6"/>' +
      '<path d="M11.4 8.6h3.6"/>'
    ),
    // Clé à molette — icône générique, pour toute prestation sans dessin dédié.
    autre: ico(
      '<path d="M14.7 6.3a4 4 0 0 0 5.2 5.2l-8.4 8.4a2.1 2.1 0 0 1-3-3z"/>' +
      '<path d="M14.7 6.3 17.9 3a1 1 0 0 1 1.4 0l1.7 1.7a1 1 0 0 1 0 1.4l-3.3 3.2"/>'
    ),

    // ── Saisons du tiroir pneu ─────────────────────────────────────────────────────────────

    // Soleil.
    ete: ico(
      '<circle cx="12" cy="12" r="4.2"/>' +
      '<path d="M12 2.2v2.4M12 19.4v2.4M2.2 12h2.4M19.4 12h2.4"/>' +
      '<path d="M5.1 5.1 6.8 6.8M17.2 17.2l1.7 1.7M18.9 5.1l-1.7 1.7M6.8 17.2l-1.7 1.7"/>'
    ),
    // Flocon : trois axes croisés, avec les barbules du seul axe vertical (les six alourdiraient
    // trop le dessin à cette taille).
    hiver: ico(
      '<path d="M12 2.6v18.8M4 7.3l16 9.4M20 7.3 4 16.7"/>' +
      '<path d="m9.3 5 2.7 2.6L14.7 5M9.3 19l2.7-2.6 2.7 2.6"/>'
    ),
    // 4 saisons : un demi-soleil et un demi-flocon, le compromis des deux.
    '4saisons': ico(
      '<circle cx="8" cy="9" r="2.9"/>' +
      '<path d="M8 3.2v1.6M8 13.2v1.6M2.2 9h1.6M12.2 9h1.6"/>' +
      '<path d="M16.8 11.4v9.2M13 13.6l7.6 4.8M20.6 13.6 13 18.4"/>'
    ),
    // « Toutes » : pas une saison, l'absence de préférence — quatre cases, donc tout.
    toutes: ico(
      '<rect x="3.4" y="3.4" width="7" height="7" rx="1.4"/>' +
      '<rect x="13.6" y="3.4" width="7" height="7" rx="1.4"/>' +
      '<rect x="3.4" y="13.6" width="7" height="7" rx="1.4"/>' +
      '<rect x="13.6" y="13.6" width="7" height="7" rx="1.4"/>'
    ),

    // ── Interface ──────────────────────────────────────────────────────────────────────────

    // Information — pastilles « Où trouver mes dimensions ? ».
    info: ico('<circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><path d="M12 7.8h.01"/>'),
    // Panier de prestations.
    panier: ico(
      '<path d="M2.6 3.4h2.6l2.3 11.2a1.6 1.6 0 0 0 1.6 1.3h8.4a1.6 1.6 0 0 0 1.6-1.3l1.3-6.8H6.1"/>' +
      '<circle cx="9.4" cy="20" r="1.3"/><circle cx="17.6" cy="20" r="1.3"/>'
    ),
    // Validation — écran de confirmation d'envoi.
    valide: ico('<circle cx="12" cy="12" r="9"/><path d="m7.8 12.3 2.8 2.8 5.6-5.9"/>'),
    // Alerte — message d'erreur du panier pneus.
    alerte: ico(
      '<path d="M10.6 3.6 2.4 17.9a1.6 1.6 0 0 0 1.4 2.4h16.4a1.6 1.6 0 0 0 1.4-2.4L13.4 3.6a1.6 1.6 0 0 0-2.8 0z"/>' +
      '<path d="M12 9.2v4"/><path d="M12 16.6h.01"/>'
    ),
    // Ajout d'une prestation au panier.
    plus: ico('<path d="M12 5.2v13.6M5.2 12h13.6"/>'),
    // Calendrier — bouton flottant « Demander un rendez-vous ».
    calendrier: ico(
      '<rect x="3.2" y="5" width="17.6" height="15.8" rx="2.2"/>' +
      '<path d="M3.2 9.6h17.6"/><path d="M8.2 2.8v4M15.8 2.8v4"/>' +
      '<path d="M7.6 13.4h3v3h-3z"/>'
    ),
    // Document chiffré — bouton flottant « Devis gratuit ».
    devis: ico(
      '<path d="M14 2.8H6.6a2 2 0 0 0-2 2v14.4a2 2 0 0 0 2 2h10.8a2 2 0 0 0 2-2V8.2z"/>' +
      '<path d="M14 2.8v4.2a1.4 1.4 0 0 0 1.4 1.4h4"/>' +
      '<path d="M8.4 13.2h7.2M8.4 16.8h4.6"/>'
    ),
  };

  window.MautoIcones = {
    ico: ico,
    ICONES: ICONES,
    // Renvoie le SVG d'une clé, ou celui de `secours` si la clé est inconnue. Sans secours, une
    // chaîne vide : mieux vaut un libellé sans icône qu'un carré vide ou un point d'interrogation.
    pour: function (cle, secours) {
      return ICONES[cle] || (secours ? (ICONES[secours] || '') : '');
    },
  };
})();
